import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  voucherService,
  VOUCHER_AMOUNTS,
  MIN_CUSTOM_AMOUNT,
  MAX_CUSTOM_AMOUNT,
  formatCurrency,
  type PurchaseVoucherRequest,
  type PurchaseVoucherResponse,
} from '../../services/voucherService';
import { API_BASE_URL } from '../../config/api.config';
import { useAuth } from '../../contexts/AuthContext';
import './VoucherPurchase.css';

interface EnabledPaymentMethods {
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  klarnaEnabled: boolean;
  bankTransferEnabled: boolean;
}

interface FormData {
  amount: number;
  customAmount: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  recipientName: string;
  recipientEmail: string;
  personalMessage: string;
  paymentMethod: 'paypal' | 'banktransfer' | 'stripe' | 'klarna';
}

const VoucherPurchase: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    amount: 100,
    customAmount: '',
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    recipientName: '',
    recipientEmail: '',
    personalMessage: '',
    paymentMethod: 'paypal',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseResult, setPurchaseResult] = useState<PurchaseVoucherResponse | null>(null);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [enabledMethods, setEnabledMethods] = useState<EnabledPaymentMethods>({
    stripeEnabled: false,
    paypalEnabled: true,
    klarnaEnabled: false,
    bankTransferEnabled: true
  });

  // Pre-fill form with user data if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        buyerName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        buyerEmail: user.email || '',
      }));
    }
  }, [isAuthenticated, user]);

  // Load enabled payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/systemsettings/payment-methods`);
        if (response.ok) {
          const data = await response.json();
          setEnabledMethods(data);

          // Set default payment method to first available
          if (data.paypalEnabled) {
            setFormData(prev => ({ ...prev, paymentMethod: 'paypal' }));
          } else if (data.stripeEnabled) {
            setFormData(prev => ({ ...prev, paymentMethod: 'stripe' }));
          } else if (data.klarnaEnabled) {
            setFormData(prev => ({ ...prev, paymentMethod: 'klarna' }));
          } else if (data.bankTransferEnabled) {
            setFormData(prev => ({ ...prev, paymentMethod: 'banktransfer' }));
          }
        }
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        // Keep defaults on error
      }
    };
    fetchPaymentMethods();
  }, []);

  // Check for PayPal return
  useEffect(() => {
    const voucherId = searchParams.get('voucherId');
    const token = searchParams.get('token'); // PayPal token

    if (voucherId && token) {
      handlePayPalReturn(parseInt(voucherId), token);
    }
  }, [searchParams]);

  const handlePayPalReturn = async (voucherId: number, paypalToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await voucherService.capturePayPalOrder(voucherId, paypalToken);
      if (result.success) {
        navigate(`/gutschein-kaufen/success?code=${result.voucherCode}&amount=${result.amount}`);
      } else {
        setError(result.message || 'PayPal-Zahlung fehlgeschlagen');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler bei der PayPal-Zahlung');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountSelect = (amount: number) => {
    setFormData(prev => ({ ...prev, amount }));
    setUseCustomAmount(false);
  };

  const handleCustomAmountChange = (value: string) => {
    setFormData(prev => ({ ...prev, customAmount: value }));
    setUseCustomAmount(true);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= MIN_CUSTOM_AMOUNT && numValue <= MAX_CUSTOM_AMOUNT) {
      setFormData(prev => ({ ...prev, amount: numValue }));
    }
  };

  // Generate preview PDF
  const handlePreviewPdf = async () => {
    setIsGeneratingPreview(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/vouchers/preview-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: selectedAmount,
          buyerName: formData.buyerName || undefined,
          buyerEmail: formData.buyerEmail || undefined,
          recipientName: formData.recipientName || undefined,
          recipientEmail: formData.recipientEmail || undefined,
          personalMessage: formData.personalMessage || undefined,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Gutschein-Vorschau.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Fehler beim Erstellen der Vorschau');
      }
    } catch (err) {
      setError('Fehler beim Erstellen der Vorschau');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate
      if (!formData.buyerName || !formData.buyerEmail) {
        throw new Error('Bitte geben Sie Ihren Namen und E-Mail-Adresse ein');
      }

      if (formData.amount < MIN_CUSTOM_AMOUNT || formData.amount > MAX_CUSTOM_AMOUNT) {
        throw new Error(`Betrag muss zwischen ${formatCurrency(MIN_CUSTOM_AMOUNT)} und ${formatCurrency(MAX_CUSTOM_AMOUNT)} liegen`);
      }

      const request: PurchaseVoucherRequest = {
        amount: formData.amount,
        buyerName: formData.buyerName,
        buyerEmail: formData.buyerEmail,
        buyerPhone: formData.buyerPhone || undefined,
        recipientName: formData.recipientName || undefined,
        recipientEmail: formData.recipientEmail || undefined,
        personalMessage: formData.personalMessage || undefined,
        paymentMethod: formData.paymentMethod,
      };

      const result = await voucherService.purchaseVoucher(request);
      setPurchaseResult(result);

      if (formData.paymentMethod === 'paypal') {
        await initiatePayPalPayment(result.voucherId);
      } else {
        // Bank transfer - show instructions with voucherId for invoice download
        navigate(`/gutschein-kaufen/vorkasse?code=${result.voucherCode}&amount=${result.amount}&voucherId=${result.voucherId}`);
      }
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  const initiatePayPalPayment = async (voucherId: number) => {
    const baseUrl = window.location.origin;
    const returnUrl = `${baseUrl}/gutschein-kaufen/success`;
    const cancelUrl = `${baseUrl}/gutschein-kaufen?cancelled=true`;

    const paypalOrder = await voucherService.createPayPalOrder(voucherId, returnUrl, cancelUrl);

    if (paypalOrder.approvalUrl) {
      window.location.href = paypalOrder.approvalUrl;
    } else {
      throw new Error('PayPal-Bestellung konnte nicht erstellt werden');
    }
  };

  const selectedAmount = useCustomAmount
    ? parseFloat(formData.customAmount) || 0
    : formData.amount;

  return (
    <div className="voucher-purchase-page">
      <div className="voucher-purchase-container">
        <div className="voucher-header">
          <img src="/logo-frankenfloss.svg" alt="Frankenfloß Logo" className="voucher-logo" />
          <h1>Verschenke eine Floßfahrt</h1>
          <p className="voucher-subtitle">
            Mit einem Frankenfloss-Gutschein verschenken Sie unvergessliche Erlebnisse auf dem Wasser.
            3 Jahre gültig!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="voucher-form">
          {error && (
            <div className="voucher-error">
              <span className="error-icon">!</span>
              {error}
            </div>
          )}

          {/* Amount Selection */}
          <section className="voucher-section">
            <h2>1. Betrag wählen</h2>
            <div className="amount-buttons">
              {VOUCHER_AMOUNTS.map(amount => (
                <button
                  key={amount}
                  type="button"
                  className={`amount-btn ${formData.amount === amount && !useCustomAmount ? 'selected' : ''}`}
                  onClick={() => handleAmountSelect(amount)}
                >
                  {formatCurrency(amount)}
                </button>
              ))}
            </div>
            <div className="custom-amount">
              <label>Oder Wunschbetrag:</label>
              <div className="custom-amount-input">
                <input
                  type="number"
                  min={MIN_CUSTOM_AMOUNT}
                  max={MAX_CUSTOM_AMOUNT}
                  placeholder="Wunschbetrag"
                  value={formData.customAmount}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  className={useCustomAmount ? 'active' : ''}
                />
                <span className="currency-symbol">EUR</span>
              </div>
              <small>Min. {formatCurrency(MIN_CUSTOM_AMOUNT)} - Max. {formatCurrency(MAX_CUSTOM_AMOUNT)}</small>
            </div>
          </section>

          {/* Recipient (Optional) */}
          <section className="voucher-section">
            <h2>2. Empfänger (optional)</h2>
            <p className="section-hint">
              Wenn Sie den Gutschein verschenken möchten, können Sie hier die Daten des Beschenkten eingeben.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label>Name des Beschenkten</label>
                <input
                  type="text"
                  placeholder="z.B. Max Mustermann"
                  value={formData.recipientName}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>E-Mail des Beschenkten</label>
                <input
                  type="email"
                  placeholder="max@example.com"
                  value={formData.recipientEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Persönliche Nachricht <span className="hint-inline">(max. 3 Zeilen)</span></label>
              <textarea
                placeholder="Ihre persönliche Nachricht an den Beschenkten..."
                value={formData.personalMessage}
                onChange={(e) => {
                  const lines = e.target.value.split('\n');
                  if (lines.length <= 3) {
                    setFormData(prev => ({ ...prev, personalMessage: e.target.value }));
                  }
                }}
                onKeyDown={(e) => {
                  const lines = formData.personalMessage?.split('\n') || [];
                  if (e.key === 'Enter' && lines.length >= 3) {
                    e.preventDefault();
                  }
                }}
                rows={3}
                maxLength={300}
              />
            </div>
          </section>

          {/* Buyer Info */}
          <section className="voucher-section">
            <h2>3. Ihre Daten</h2>
            <div className="form-row">
              <div className="form-group required">
                <label>Ihr Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Ihr vollständiger Name"
                  value={formData.buyerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, buyerName: e.target.value }))}
                />
              </div>
              <div className="form-group required">
                <label>Ihre E-Mail *</label>
                <input
                  type="email"
                  required
                  placeholder="ihre.email@example.com"
                  value={formData.buyerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, buyerEmail: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Telefon (optional)</label>
              <input
                type="tel"
                placeholder="+49 123 456789"
                value={formData.buyerPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, buyerPhone: e.target.value }))}
              />
            </div>
          </section>

          {/* Payment Method */}
          <section className="voucher-section">
            <h2>4. Zahlungsart</h2>
            <div className="payment-methods">
              {enabledMethods.paypalEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'paypal' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="paypal"
                    checked={formData.paymentMethod === 'paypal'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'paypal' }))}
                  />
                  <div className="payment-content">
                    <img src="/paypal-logo.svg" alt="PayPal" className="payment-logo" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    <span>PayPal</span>
                    <small>Sofortige Aktivierung nach Zahlung</small>
                  </div>
                </label>
              )}

              {enabledMethods.stripeEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'stripe' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="stripe"
                    checked={formData.paymentMethod === 'stripe'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'stripe' }))}
                  />
                  <div className="payment-content">
                    <span className="stripe-icon">Kreditkarte</span>
                    <span>Kreditkarte</span>
                    <small>Visa, Mastercard, AMEX</small>
                  </div>
                </label>
              )}

              {enabledMethods.klarnaEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'klarna' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="klarna"
                    checked={formData.paymentMethod === 'klarna'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'klarna' }))}
                  />
                  <div className="payment-content">
                    <span className="klarna-icon">Klarna</span>
                    <span>Klarna</span>
                    <small>Rechnung &amp; Ratenzahlung</small>
                  </div>
                </label>
              )}

              {enabledMethods.bankTransferEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'banktransfer' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="banktransfer"
                    checked={formData.paymentMethod === 'banktransfer'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'banktransfer' }))}
                  />
                  <div className="payment-content">
                    <span className="bank-icon">Bank</span>
                    <span>Vorkasse / Überweisung</span>
                    <small>Aktivierung nach Zahlungseingang (1-3 Werktage)</small>
                  </div>
                </label>
              )}
            </div>
          </section>

          {/* Summary */}
          <section className="voucher-summary">
            <h2>Zusammenfassung</h2>
            <div className="summary-content">
              <div className="summary-row">
                <span>Gutschein-Betrag:</span>
                <strong>{formatCurrency(selectedAmount)}</strong>
              </div>
              <div className="summary-row total">
                <span>Zu zahlen:</span>
                <strong>{formatCurrency(selectedAmount)}</strong>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="voucher-actions">
            <button
              type="button"
              className="voucher-preview-btn"
              onClick={handlePreviewPdf}
              disabled={isGeneratingPreview || selectedAmount < MIN_CUSTOM_AMOUNT}
            >
              {isGeneratingPreview ? 'Wird erstellt...' : 'Vorschau'}
            </button>
            <button
              type="submit"
              className="voucher-submit-btn"
              disabled={isLoading || selectedAmount < MIN_CUSTOM_AMOUNT}
            >
              {isLoading ? (
                <span className="loading-spinner">Wird bearbeitet...</span>
              ) : formData.paymentMethod === 'paypal' ? (
                'Weiter zu PayPal'
              ) : formData.paymentMethod === 'stripe' ? (
                'Weiter zur Kartenzahlung'
              ) : formData.paymentMethod === 'klarna' ? (
                'Weiter zu Klarna'
              ) : (
                'Gutschein kaufen (Vorkasse)'
              )}
            </button>
          </div>

          <p className="voucher-legal">
            Mit dem Kauf akzeptieren Sie unsere <a href="/agb" target="_blank">AGB</a> und <a href="/datenschutz" target="_blank">Datenschutzerklärung</a>.
            Gutscheine sind 3 Jahre ab Kaufdatum gültig.
          </p>
        </form>
      </div>
    </div>
  );
};

export default VoucherPurchase;
